import { useState } from "react";
import { useIntl } from "react-intl";

import { Button } from "@/components/Button/Button";
import {
  DrawerContent,
  DrawerFooter,
  DrawerTopBar,
  SwipeableDrawer,
} from "@/components/Drawer";
import { Radio } from "@/components/Radio/Radio";
import { RadioGroup } from "@/components/Radio/RadioGroup";
import type { MappedServiceData } from "@/features/flights/components/additional-services/types";
import { formatCurrency } from "@/utils/currency";

interface Props {
  open: boolean;
  onClose: () => void;
  onConfirm: (service: MappedServiceData | null, optionCode: string) => void;
  selectedOptionCode?: string;
  service: MappedServiceData | null;
  headerTitle: React.ReactNode;
}

export const MealSelectionDrawer = ({
  open,
  service,
  selectedOptionCode,
  headerTitle,
  onConfirm,
  onClose,
}: Props) => {
  const intl = useIntl();
  const [value, setValue] = useState(selectedOptionCode ?? "");

  return (
    <SwipeableDrawer
      open={open}
      onClose={onClose}
      backgroundColor="background"
      hasFullHeight
      heightOffset={96}
    >
      <DrawerTopBar
        onCloseClick={onClose}
        title={intl.formatMessage({
          defaultMessage: "Meal selection",
          description: "Section heading for meal selection for a traveller",
        })}
        subtitle={headerTitle}
      />

      <DrawerContent>
        <div className="flex flex-col rounded-3xl bg-white p-4">
          <RadioGroup
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="-my-3 divide-y divide-stroke-neutral-soft *:py-3"
          >
            {service?.service.options.map((option) => {
              return (
                <Radio
                  key={option.code}
                  value={option.code}
                  className="w-full"
                  labelPlacement="start"
                  label={
                    <div className="flex w-full flex-col gap-1 text-text-primary">
                      <div className="font-medium">{option.description}</div>
                      {option.price.amount !== null ? (
                        <div className="text-sm font-normal text-text-secondary">
                          {formatCurrency(
                            option.price.amount,
                            option.price.currency || undefined,
                          )}
                        </div>
                      ) : null}
                    </div>
                  }
                />
              );
            })}
            {service?.mandatory ? null : (
              <Radio
                value=""
                className="w-full"
                labelPlacement="start"
                label={
                  <div className="flex w-full flex-col gap-1 text-text-primary">
                    <div className="font-medium">
                      {intl.formatMessage({
                        defaultMessage: "No additional food",
                        description:
                          "Option label indicating no additional food or meal is selected",
                      })}
                    </div>
                    <div className="text-sm font-normal text-text-secondary">
                      {formatCurrency(0)}
                    </div>
                  </div>
                }
              />
            )}
          </RadioGroup>
        </div>
      </DrawerContent>

      <DrawerFooter>
        <Button
          fullWidth
          variant="contained"
          size="medium"
          onClick={() => {
            onConfirm(service, value);
            onClose();
          }}
        >
          {intl.formatMessage({
            defaultMessage: "Confirm",
            description:
              "Generic 'Confirm' button label to submit or accept an action",
          })}
        </Button>
      </DrawerFooter>
    </SwipeableDrawer>
  );
};
